// =============================================================================
// DirectedFreeTextQuestionCard — pergunta de um questionário de resposta livre
//
// Mostra o texto da pergunta (e a imagem de apoio, se houver) e um campo de texto
// para a pessoa escrever a resposta como quiser. Sem conformidade nem plano de ação.
// =============================================================================

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Textarea } from '@/components/ui/textarea'
import type { DirecionadaInput } from '@/schemas/direcionada-schema'
import { INSPECTION_COLORS } from '../../constants/inspection-colors'
import type { DirectedQuestion } from './directed-question-card'
import { InspectionPhotoField } from './inspection-photo-field'

// Mesmo limite do backend (MAX_TEXT_ANSWER_LENGTH)
const MAX_TEXT_ANSWER_LENGTH = 5000

export interface DirectedFreeTextQuestionCardProps {
	question: DirectedQuestion
	index: number
	answerIndex: number
}

export const DirectedFreeTextQuestionCard = ({
	question,
	index,
	answerIndex,
}: DirectedFreeTextQuestionCardProps) => {
	const { t } = useTranslation()
	const {
		register,
		watch,
		formState: { errors },
	} = useFormContext<DirecionadaInput>()
	const fieldName = `answers.${answerIndex}.textAnswer` as const
	const value = watch(fieldName) ?? ''
	const error = errors.answers?.[answerIndex]?.textAnswer?.message

	return (
		<div className="space-y-3 rounded-lg border border-kaefer-light-silver px-2 py-4">
			<div className="flex items-start gap-3">
				<span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-kaefer-ocean-blue/10 text-xs font-bold text-kaefer-ocean-blue">
					{index}
				</span>
				<p className="pt-0.5 font-medium text-kaefer-dark-grey text-sm leading-snug">
					{question.title}
				</p>
			</div>

			{question.imagePath && (
				<div className="flex justify-center">
					<InspectionPhotoField
						attachLabel=""
						altText={t('inspections.directed.questionImageSelected')}
						preview={question.imagePath}
						onFileChange={() => {}}
						onRemove={() => {}}
						colors={INSPECTION_COLORS.oceanBlue}
						showRemoveButton={false}
						showPickerWhenEmpty={false}
						hideOnPreviewError
					/>
				</div>
			)}

			<div className="space-y-1">
				<Textarea
					{...register(fieldName)}
					aria-invalid={Boolean(error)}
					maxLength={MAX_TEXT_ANSWER_LENGTH}
					placeholder={t('inspections.directed.freeTextPlaceholder')}
					rows={5}
					className="resize-y"
				/>
				<div className="flex items-center justify-between text-xs">
					<span className="text-destructive">{error}</span>
					<span className="text-muted-foreground tabular-nums">
						{value.length}/{MAX_TEXT_ANSWER_LENGTH}
					</span>
				</div>
			</div>
		</div>
	)
}
